import * as dateFns from 'date-fns';

import { getReadableTime } from '../../../utils';

/**
 * We want to use the same 10 minute range in all locations in the app.
 * We should round up to the next 5 minute increment.
 *
 * Examples:
 *    For inputDate = 11:03, we want 11:05-11:15.
 *    For inputDate = 11:05, we want 11:05-11:15.
 *    For inputDate = 11:07, we want 11:10-11:20.
 *    For inputDate = 11:10, we want 11:10-11:20.
 */

export const getOrderTimeRanges = ({
  wantedTime,
  estimatedDeliveryTime,
}: OrderTimeRangesProps) => {
  const wantedTimeRange = getWantedTimeRange(wantedTime);
  const estimatedDeliveryTimeRange = getEstimatedDeliveryTimeRange(
    estimatedDeliveryTime,
  );

  return {
    ...wantedTimeRange,
    ...estimatedDeliveryTimeRange,
  };
};

const getWantedTimeRange = (wantedTime?: Date) => {
  const timeRange = getTimeRange(wantedTime);

  if (!timeRange) return {};

  const { startTime: wantedTimeStart, endTime: wantedTimeEnd } = timeRange;

  return { wantedTimeStart, wantedTimeEnd };
};

const getEstimatedDeliveryTimeRange = (wantedTime?: Date) => {
  const timeRange = getTimeRange(wantedTime);

  if (!timeRange) return {};

  const {
    startTime: estimatedDeliveryTimeStart,
    endTime: estimatedDeliveryTimeEnd,
  } = timeRange;

  return { estimatedDeliveryTimeStart, estimatedDeliveryTimeEnd };
};

const getTimeRange = (inputDate?: Date) => {
  if (!inputDate) return;

  const startDate = getNextTimeRangeRoundedUp(inputDate);
  const endDate = dateFns.addMinutes(startDate, TIME_RANGE_OFFSET);

  return {
    startTime: getReadableTime(startDate),
    endTime: getReadableTime(endDate),
  };
};

const getNextTimeRangeRoundedUp = (inputDate: Date) => {
  return new Date(
    Math.ceil(inputDate.getTime() / ROUNDED_TO_MINUTES) * ROUNDED_TO_MINUTES,
  );
};

const ROUNDED_TO_MINUTES = 5 * 60 * 1000; // 5 mins in milliseconds.
const TIME_RANGE_OFFSET = 10; // 10 mins.

type OrderTimeRangesProps = {
  readonly wantedTime?: Date;
  readonly estimatedDeliveryTime?: Date;
};
